<template>
    <div :class="theme.wrapper">
        <div :class="theme.left">
            <div :class="theme.controls">
                <div class="btn-group">
                    <button
                        v-if="config.print_enabled"
                        type="button"
                        class="btn btn-default btn-sm"
                        title="Print / Export"
                        :class="theme.printMenu"
                        @click="showPrintMenu"
                    >
                        <i class="fa-solid fa-print" />
                    </button>

                    <button
                        v-if="showFilterMenu"
                        type="button"
                        class="btn btn-default btn-sm"
                        title="Filter Menu"
                        :class="{
                            [theme.filterMenu]: true,
                            [theme.active]: hasActiveFilters,
                        }"
                        @click.stop="toggleFilterMenu"
                    >
                        <i class="fa-solid fa-filter" />
                    </button>

                    <button
                        v-if="config.trash_enabled"
                        type="button"
                        class="btn btn-default btn-sm"
                        :class="{
                            [theme.trashToggle]: true,
                            [theme.active]: props.show_trashed,
                        }"
                        :title="
                            props.show_trashed
                                ? 'Close Trashed'
                                : 'View Trashed'
                        "
                        @click="toggleShowTrashed"
                    >
                        <i class="fa-solid fa-trash" />
                    </button>

                    <button
                        v-if="config.show_reload_button"
                        type="button"
                        class="btn btn-default btn-sm"
                        title="Reload"
                        @click="reload({ message: 'Reload complete' })"
                    >
                        <i class="fa-solid fa-sync-alt" />
                    </button>
                </div>

                <app-filter-menu
                    v-if="data.filterMenuVisible"
                    :data="data"
                    :filters="filterMenuFilters"
                    @close="toggleFilterMenu"
                />
            </div>

            <app-search-filter
                v-if="config.search_enabled"
                :data="data"
                :filter="getFilterByName('search')"
            />

            <app-select-list-filter
                v-for="filter in headerFilters"
                :key="`filter-${filter.name}`"
                :data="data"
                :filter="filter"
            />

            <div
                v-visible="hasFilterDisplayData"
                :class="theme.activeFiltersDisplay"
            >
                <label>Filters:</label>

                <span
                    v-for="(filter, index) in filterDisplayData"
                    :key="`filter-display-data-${filter.label}-${filter.key}`"
                >
                    <span v-if="index > 0" :class="theme.separator">{{
                        filter.separator ? filter.separator : 'AND'
                    }}</span>
                    <span :class="theme.label">{{ filter.label }}</span>
                    <span :class="theme.operator">{{ filter.operator }}</span>
                    <span
                        v-popover="filter.expandedValue"
                        :class="theme.value"
                        >{{ filter.value }}</span
                    >
                </span>
            </div>
        </div>

        <div :class="theme.middle">
            <h3>{{ data.config.title }}</h3>

            <div v-if="props.show_trashed" :class="theme.trashed">
                - Trashed Items -
            </div>
        </div>

        <div :class="theme.right">
            <app-button-control
                v-if="config.buttons_location === 'top-right'"
                :data="data"
            />
            <app-page-size-control v-else :data="data" />
        </div>

        <app-print-menu
            v-show="printMenuVisible"
            ref="printMenu"
            :data="data"
        />
    </div>
</template>

<script>
import FilterMenu from './FilterMenu.vue';
import SelectListFilter from './SelectListFilter.vue';
import SearchFilter from './SearchFilter.vue';
import PrintMenu from './PrintMenu.vue';
import PageSizeControl from './PageSizeControl.vue';
import ButtonControl from './ButtonControl.vue';
import Common from './mixins/Common.vue';
import ReloadsGrid from './mixins/ReloadsGrid.vue';
import UsesFilters from './mixins/UsesFilters.vue';

export default {
    components: {
        'app-filter-menu': FilterMenu,
        'app-search-filter': SearchFilter,
        'app-print-menu': PrintMenu,
        'app-page-size-control': PageSizeControl,
        'app-button-control': ButtonControl,
        'app-select-list-filter': SelectListFilter,
    },
    mixins: [Common, ReloadsGrid, UsesFilters],
    props: {
        data: Object,
    },
    data() {
        return {
            printMenuVisible: false,
        };
    },
    computed: {
        filterDisplayData() {
            return this.activeFilters.flatMap(filter => filter.display_data);
        },
        hasFilterDisplayData() {
            return this.filterDisplayData.length > 0;
        },
        showFilterMenu() {
            return this.filterMenuFilters.length > 0;
        },
        filterMenuFilters() {
            return this.props.filters.filter(
                x => x.type !== 'search' && x.location !== 'header',
            );
        },
        headerFilters() {
            return this.props.filters.filter(x => x.location === 'header');
        },
    },
    mounted() {
        document.querySelector('body').addEventListener('click', () => {
            this.toggleFilterMenu(false);
        });

        const updateHeight = () =>
            (this.data.headerHeight = $(this.$el).outerHeight());
        updateHeight();

        if ('ResizeObserver' in window) {
            const observer = new ResizeObserver(updateHeight);
            observer.observe(this.$el);
            this.$once('hook:destroyed', () => {
                observer.disconnect();
            });
        }
    },
    methods: {
        toggleShowTrashed() {
            this.setProps('show_trashed', !this.props.show_trashed);
            this.reload();
        },
        showPrintMenu() {
            this.printMenuVisible = true; // IMPORTANT: For some weird reason if the component is not initially hidden
            // using v-show the entire page breaks without any error message
            this.$refs.printMenu.show();
        },
        toggleFilterMenu(isVisible = null) {
            if (typeof isVisible === 'boolean') {
                this.data.filterMenuVisible = isVisible;
            } else {
                this.data.filterMenuVisible = !this.data.filterMenuVisible;
            }
        },
        getFilterByName(name) {
            return this.props.filters.find(x => x.name === name);
        },
    },
};
</script>

<style lang="scss" module></style>
<style
    lang="scss"
    module="theme_default"
    src="./../css/themes/default/header.scss"
></style>
<style
    lang="scss"
    module="theme_simple"
    src="./../css/themes/simple/header.scss"
></style>
